// The UI thread's handle on the data worker: typed calls, one change stream. Views and view
// models import `data` and never `fetch` or SQL — docs/PERFORMANCE.md's layer split, kept honest
// by there being no other way in.
import type { StoreTable } from '../data/db/database';
import type { EngineSignal } from '../data/sync/syncEngine';
import type { Method, RequestMessage, ResponseMessage, WorkerApi, WorkerEvent } from './protocol';
import { count } from '../ui/perf';
import { DlpBlock } from '../core/dlpBlock';

type ChangeListener = (tables: Set<StoreTable>) => void;

/** A request dropped on purpose (tab handed over, page hidden): moot, not a fault — errorReporting skips it. */
export function workerStoppedError(): Error {
  return Object.assign(new Error('worker stopped'), { name: 'WorkerStopped' });
}
type SignalListener = (signal: EngineSignal) => void;

/**
 * Another page's DataClient, reached through `window.opener` — the thread window (PARITY P317,
 * ui/features/extras/threadWindow.tsx). Only one tab may own the database, so a popped-out thread
 * borrows the opener's worker instead of starting one.
 */
export interface RemoteDataClient {
  call(method: string, args: unknown[]): Promise<unknown>;
  onChanges(listener: (tables: Set<StoreTable>) => void): () => void;
  onSignal(listener: (signal: EngineSignal) => void): () => void;
}

class DataClient {
  private worker: Worker | null = null;
  private remote: RemoteDataClient | null = null;
  private nextId = 1;
  private pending = new Map<number, { resolve: (v: unknown) => void; reject: (e: Error) => void }>();
  private changeListeners = new Set<ChangeListener>();
  private signalListeners = new Set<SignalListener>();
  /** The page was hidden (pagehide) and its worker stopped; only a reload brings the data back. */
  private pageGone = false;
  /** The newest connection state, for a screen that subscribes after the socket already moved. */
  lastConnection: Extract<EngineSignal, { kind: 'connection' }> | null = null;

  /** This page borrows another page's worker (the thread window); never starts its own. */
  useRemote(remote: RemoteDataClient): void {
    this.remote = remote;
  }

  get isRemote(): boolean {
    return this.remote !== null;
  }

  start(): void {
    if (this.worker || this.remote) return;
    this.worker = new Worker(new URL('../data/worker.ts', import.meta.url), { type: 'module', name: 'satori-data' });
    this.worker.onmessage = (event: MessageEvent<ResponseMessage | WorkerEvent>) => {
      const msg = event.data;
      if ('event' in msg) {
        if (msg.event === 'dlpBlocked') {
          DlpBlock.notify();
        } else if (msg.event === 'changes') {
          const set = new Set(msg.tables);
          count('changes');
          for (const t of set) count('changes.' + t);
          for (const l of this.changeListeners) l(set);
        } else {
          if (msg.signal.kind === 'connection') this.lastConnection = msg.signal;
          for (const l of this.signalListeners) l(msg.signal);
        }
        return;
      }
      const waiter = this.pending.get(msg.id);
      if (!waiter) return;
      this.pending.delete(msg.id);
      if (msg.ok) waiter.resolve(msg.value);
      else waiter.reject(new Error(msg.error));
    };
    this.worker.onerror = (e) => console.error('[satori] data worker error', e.message);
    // Release the database before the page unloads (see worker.ts `shutdown`).
    // Stop the worker OURSELVES, synchronously, when the page goes away. Left to the browser's own
    // teardown, a page unloaded while the worker was writing through the OPFS pool crashed the tab
    // ("Aw, Snap" — reproduced every time by navigating ~1 s after login, mid first sync); terminated
    // here it never did (3/3, same scenario). An interrupted write is safe: SQLite's journal rolls
    // the unfinished transaction back, and the server re-syncs everything on the next open.
    window.addEventListener('pagehide', () => {
      this.shutdown();
      this.worker?.terminate();
      this.worker = null;
      this.pageGone = true;
      this.rejectPending();
    });
    // iOS Safari keeps the page in the back/forward cache and brings it back (pageshow, persisted)
    // with the worker above already stopped. A call then started a NEW worker nobody had booted:
    // «not booted» / «signed out» and a dead chat (diego.coss, iPhone, 2026-11-04). The page's whole
    // state assumes a booted worker, so a restored page reloads; the store is on disk, it is quick.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted && this.pageGone) window.location.reload();
    });
  }

  private rejectPending(): void {
    const stopped = workerStoppedError();
    for (const w of this.pending.values()) w.reject(stopped);
    this.pending.clear();
  }

  shutdown(): void {
    this.worker?.postMessage({ shutdown: true });
  }

  /** Releases the database for another tab ("Usar aquí" there): shut down cleanly, then stop. */
  async terminate(): Promise<void> {
    const worker = this.worker;
    if (!worker) return;
    await Promise.race([this.call('shutdown'), new Promise((r) => setTimeout(r, 1000))]).catch(() => undefined);
    worker.terminate();
    this.worker = null;
    // Handed to another tab on purpose: what was in flight is moot, not a fault (jesus.lopez's report).
    this.rejectPending();
  }

  call<M extends Method>(method: M, ...args: Parameters<WorkerApi[M]>): Promise<ReturnType<WorkerApi[M]>> {
    if (this.remote) return this.remote.call(method, args) as Promise<ReturnType<WorkerApi[M]>>;
    // After pagehide no new worker: it would never be booted (see `pageshow` in start()).
    if (this.pageGone) return Promise.reject(workerStoppedError());
    if (!this.worker) this.start();
    const id = this.nextId++;
    const message: RequestMessage = { id, method, args };
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage(message);
    });
  }

  /** For a method another feature module declares (typed at its own call sites). */
  callUntyped<T = unknown>(method: string, ...args: unknown[]): Promise<T> {
    return (this.call as unknown as (m: string, ...a: unknown[]) => Promise<T>)(method, ...args);
  }

  onChanges(listener: ChangeListener): () => void {
    if (this.remote) return this.remote.onChanges(listener);
    this.changeListeners.add(listener);
    return () => this.changeListeners.delete(listener);
  }

  onSignal(listener: SignalListener): () => void {
    if (this.remote) return this.remote.onSignal(listener);
    this.signalListeners.add(listener);
    return () => this.signalListeners.delete(listener);
  }
}

export const data = new DataClient();

// MARK: - One tab owns the database

const LOCK_NAME = 'satori-chat-db';
const CHANNEL_NAME = 'satori-chat-tabs';

/**
 * Takes the database lock if it is free. Resolves true once held; false when another tab has it.
 * The lock is held until `releaseTab()` or the tab closes — the browser frees it on its own then,
 * which is what makes a crashed tab unable to lock the chat away for ever.
 */
let releaseLock: (() => void) | null = null;
let tabChannel: BroadcastChannel | null = null;

let acquiring: Promise<boolean> | null = null;

export function acquireTab(onTakenElsewhere: () => void): Promise<boolean> {
  acquiring ??= acquireTabOnce(onTakenElsewhere);
  return acquiring;
}

async function acquireTabOnce(onTakenElsewhere: () => void): Promise<boolean> {
  if (!('locks' in navigator)) return true; // very old browser: no arbitration available
  tabChannel ??= new BroadcastChannel(CHANNEL_NAME);
  const got = await new Promise<boolean>((resolve) => {
    void navigator.locks.request(LOCK_NAME, { ifAvailable: true }, (lock) => {
      if (!lock) {
        resolve(false);
        return undefined;
      }
      resolve(true);
      return new Promise<void>((release) => {
        releaseLock = release;
      });
    });
  });
  if (got) {
    tabChannel.onmessage = (e) => {
      if (e.data === 'take-over') {
        // Another tab asked for the chat: let go of everything, then the lock.
        void data.terminate().then(() => {
          releaseLock?.();
          releaseLock = null;
        });
        onTakenElsewhere();
      }
    };
  }
  return got;
}

/** "Usar aquí": ask the owning tab to let go, then wait for the lock. */
export async function takeOverTab(onTakenElsewhere: () => void): Promise<void> {
  tabChannel ??= new BroadcastChannel(CHANNEL_NAME);
  tabChannel.postMessage('take-over');
  await new Promise<void>((resolve) => {
    void navigator.locks.request(LOCK_NAME, (lock) => {
      resolve();
      return new Promise<void>((release) => {
        releaseLock = release;
        void lock;
      });
    });
  });
  tabChannel.onmessage = (e) => {
    if (e.data === 'take-over') {
      void data.terminate().then(() => {
        releaseLock?.();
        releaseLock = null;
      });
      onTakenElsewhere();
    }
  };
}
